import React, { useCallback, useMemo, useState } from 'react';
import { useSmsAuth } from '../../hooks/smsAuth';
import { ImportUserParams, SignInParams, VerifyParams } from '../../../services/identity/identity.types';
import { useBoolean } from '@sravni/react-utils';

type AuthSmsContext = {
    countdown: number;
    codeIsRequested: boolean;
    requestCode: (params: SignInParams) => Promise<void>;
    checkCode: (params: VerifyParams & Partial<ImportUserParams>) => Promise<string | null>;
    inProcess: boolean;
    errorText: string;
    attempts: number;
    isSilentAuth: boolean;
    showSmsField: boolean;
    setShowingSmsField: (show: boolean) => void;
};

export const AuthSmsContext = React.createContext<AuthSmsContext>({} as AuthSmsContext);

/** Создается один раз, уровнем выше всех шагов-компонентов. Далее - используем контекст */
export const useAuthSmsContext = (withSilentAuth?: boolean) => {
    const {
        countdown,
        codeIsRequested,
        requestCode: request,
        checkCode: check,
        inProcess,
        errorText,
        attempts,
    } = useSmsAuth();

    const [isSilentAuth, _] = useBoolean(Boolean(withSilentAuth));
    const [showSmsField, setShowSmsField] = useState(false);

    const requestCode = useCallback(
        async (params: SignInParams) => {
            return await request(params);
        },
        [request],
    );

    const checkCode = useCallback(
        async (params: VerifyParams & Partial<ImportUserParams>) => {
            return await check(params, withSilentAuth);
        },
        [request, withSilentAuth],
    );

    const setShowingSmsField = useCallback(
        (show: boolean) => {
            setShowSmsField(show);
        },
        [setShowSmsField],
    );

    return useMemo(
        () => ({
            countdown,
            codeIsRequested,
            requestCode,
            checkCode,
            inProcess,
            errorText,
            attempts,
            isSilentAuth,
            showSmsField,
            setShowingSmsField,
        }),
        [
            countdown,
            codeIsRequested,
            requestCode,
            checkCode,
            inProcess,
            errorText,
            attempts,
            isSilentAuth,
            showSmsField,
            setShowingSmsField,
        ],
    );
};
